import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X, Star } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { errorMessage } from '@/lib/utils';
import { useAuth } from '@/lib/auth';

// Discounts & offers: super_admin creates platform-wide offers (apply at
// every academy) by leaving the venue picker blank; an academy admin's
// offers always apply only to their own venue, same scoping pattern as
// Courts/Plans. Applied automatically at checkout — see the backend's
// app.services.best_discount — never stacked with another offer or a
// membership discount; whichever saves the customer the most wins.
const empty = {
  title: '', description: '', discount_type: 'percent', discount_value: 10,
  applies_to: 'both', active: true, featured: false, start_date: '', end_date: '',
};

export default function AdminOffers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const load = () => api.get('/admin/offers').then((r) => setRows(r.data));
  useEffect(() => {
    load();
    if (isSuperAdmin) api.get('/admin/academies').then((r) => setVenues(r.data)).catch(() => {});
  }, [isSuperAdmin]);

  const openNew = () => { setForm(empty); setEditing(null); setVenueId(''); setOpen(true); };
  const openEdit = (o) => {
    setForm({
      title: o.title, description: o.description || '', discount_type: o.discount_type,
      discount_value: o.discount_value, applies_to: o.applies_to, active: o.active,
      featured: o.featured, start_date: o.start_date || '', end_date: o.end_date || '',
    });
    setEditing(o.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        discount_value: Number(form.discount_value),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      if (editing) await api.put(`/admin/offers/${editing}`, payload);
      else await api.post('/admin/offers', payload, { params: venueId ? { academy_id: venueId } : {} });
      toast({ title: editing ? 'Offer updated' : 'Offer added', variant: 'success' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Save failed', description: errorMessage(e.response?.data?.detail), variant: 'error' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this offer?')) return;
    try { await api.delete(`/admin/offers/${id}`); toast({ title: 'Offer deleted', variant: 'success' }); load(); }
    catch (e) { toast({ title: 'Delete failed', description: errorMessage(e.response?.data?.detail), variant: 'error' }); }
  };

  const venueName = (id) => venues.find((v) => v.id === id)?.name;

  return (
    <div className="space-y-6" data-testid="admin-offers">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Discounts</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Offers</h1>
          <p className="text-sm text-white/50 mt-2 max-w-xl">
            {isSuperAdmin
              ? 'Platform-wide offers apply at every venue. Scope one to a single venue instead by picking it below.'
              : 'Offers you create here apply only to your own venue.'}
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-offer-button"><Plus size={14} /> Add Offer</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((o) => (
          <div key={o.id} className={`bg-surface border ${o.featured ? 'border-volt volt-glow' : 'border-white/10'} p-6 flex flex-col gap-3`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Badge tone={o.active ? 'success' : 'default'}>{o.active ? 'Active' : 'Hidden'}</Badge>
              <div className="flex gap-1">
                {o.featured && <Badge tone="volt"><Star size={10} className="inline mr-1" />Featured</Badge>}
                <Badge>{isSuperAdmin && !o.academy_id ? 'Platform-wide' : venueName(o.academy_id) || 'This venue'}</Badge>
              </div>
            </div>
            <h3 className="font-heading font-black text-xl uppercase">{o.title}</h3>
            <p className="text-sm text-white/50 line-clamp-2 flex-1">{o.description || '—'}</p>
            <div className="font-heading font-black text-2xl text-volt">
              {o.discount_type === 'percent' ? `${o.discount_value}% off` : `₹${o.discount_value} off`}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
              Applies to {o.applies_to === 'both' ? 'bookings & memberships' : o.applies_to}
              {(o.start_date || o.end_date) ? ` · ${o.start_date || '…'} → ${o.end_date || '…'}` : ''}
            </div>
            <div className="flex gap-2 pt-2 border-t border-white/10">
              <Button variant="outline" size="sm" onClick={() => openEdit(o)} data-testid={`edit-offer-${o.id}`}><Edit3 size={12} /> Edit</Button>
              <Button variant="danger" size="sm" onClick={() => remove(o.id)} data-testid={`delete-offer-${o.id}`}><Trash2 size={12} /></Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-white/40 text-sm col-span-full text-center py-12">No offers yet.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="offer-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl uppercase">{editing ? 'Edit' : 'New'} Offer</h2>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {!editing && isSuperAdmin && (
                <Field label="Venue" hint="Leave blank for a platform-wide offer that applies everywhere.">
                  <select
                    value={venueId}
                    onChange={(e) => setVenueId(e.target.value)}
                    className="h-12 w-full border border-white/10 bg-obsidian px-4 text-sm text-white outline-none"
                    data-testid="offer-venue"
                  >
                    <option value="">Platform-wide (all venues)</option>
                    {venues.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
                  </select>
                </Field>
              )}
              <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Diwali Special" data-testid="offer-title" /></Field>
              <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Shown on the home screen banner and at checkout." /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Discount Type">
                  <select
                    value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="h-12 w-full border border-white/10 bg-obsidian px-4 text-sm text-white outline-none"
                    data-testid="offer-discount-type"
                  >
                    <option value="percent">Percent off</option>
                    <option value="flat">Flat amount off (₹)</option>
                  </select>
                </Field>
                <Field label={form.discount_type === 'percent' ? 'Discount %' : 'Discount ₹'}>
                  <Input type="number" min="0" max={form.discount_type === 'percent' ? 100 : undefined} value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="offer-discount-value" />
                </Field>
              </div>
              <Field label="Applies to">
                <select
                  value={form.applies_to}
                  onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
                  className="h-12 w-full border border-white/10 bg-obsidian px-4 text-sm text-white outline-none"
                  data-testid="offer-applies-to"
                >
                  <option value="both">Bookings & Memberships</option>
                  <option value="booking">Bookings only</option>
                  <option value="membership">Memberships only</option>
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Start date (optional)"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="offer-start-date" /></Field>
                <Field label="End date (optional)"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="offer-end-date" /></Field>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                  <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-volt" /> Active
                </label>
                <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                  <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-volt" data-testid="offer-featured" /> Feature on home screen
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={save} className="flex-1" data-testid="save-offer">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
